import { IFramework, IWebServiceManager, Utilities as utilities, HttpMethod } from '@truckdown/framework';
import { ICityManager } from './ICityManager';
import { City } from './City';
import { Point } from './Point';
import { CitySearchOptions } from './CitySearchOptions';
import { CitySeoData } from './CitySeoData';

class CityManager implements ICityManager {
    constructor(framework: IFramework) {
        this.manager = framework.getRequiredService<IWebServiceManager>('IWebServiceManager');
    }

    readonly manager: IWebServiceManager;

    search(term: string, options?: CitySearchOptions): Promise<City[]> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: '/tools/misc/citysearch/' + encodeURIComponent(term),
            method: HttpMethod.POST
        }, options ?? ({} as CitySearchOptions));

        return new Promise<City[]>((resolve, reject) => {
            this.manager
                .get<any[]>(req)
                .then((res) => {
                    resolve(res.map(r => {
                        return new City(r);
                    }));
                })
                .catch((err) => {
                    console.log(err);
                    resolve([]);
                })
        });
    }

    get(data: string | Point | CitySeoData): Promise<City | null> {
        if (utilities.data.isString(data)) {
            return this.getFromString(data as string);
        } else if (this.isPoint(data)) {
            return this.getFromPoint(data as Point);
        } else {
            return this.getFromData(data as CitySeoData);
        }
    }

    private isPoint(data: string | Point | CitySeoData): data is Point {
        if ((data as Point).lat) {
            return true;
        }
        return false;
    }

    private getFromString(id: string): Promise<City | null> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: '/tools/misc/cities/' + encodeURIComponent(id),
            method: HttpMethod.GET
        });


        return new Promise<City | null>((resolve, reject) => {
            this.manager
                .get<any>(req)
                .then((res) => {
                    if (res == null) {
                        resolve(null);
                    } else {
                        resolve(new City(res));
                    }
                })
                .catch((err) => {
                    console.log(err);
                    resolve(null);
                })
        });
    }

    private getFromPoint(point: Point): Promise<City | null> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: '/tools/misc/cities/' + encodeURIComponent(point.lat) + '/' + encodeURIComponent(point.lng),
            method: HttpMethod.GET
        });


        return new Promise<City | null>((resolve, reject) => {
            this.manager
                .get<any>(req)
                .then((res) => {
                    if (res == null) {
                        resolve(null);
                    } else {
                        resolve(new City(res));
                    }
                })
                .catch((err) => {
                    console.log(err);
                    resolve(null);
                })
        });
    }

    private getFromData(data: CitySeoData): Promise<City | null> {
        let path = '/tools/misc/cities/' + encodeURIComponent(data.seo);
        if (data.rgn && data.rgn !== null && data.rgn.length > 0) {
            path = path + '/' + encodeURIComponent(data.rgn);
        }

        let req = this.manager.createRequest({
            service: this.manager.api,
            path: path,
            method: HttpMethod.GET
        });


        return new Promise<City | null>((resolve, reject) => {
            this.manager
                .get<any>(req)
                .then((res) => {
                    if (res == null) {
                        resolve(null);
                    } else {
                        resolve(new City(res));
                    }
                })
                .catch((err) => {
                    console.log(err);
                    resolve(null);
                })
        });
    }

    suppressSeo(id: string): Promise<City | null> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: '/tools/misc/cities/' + encodeURIComponent(id),
            method: HttpMethod.PATCH
        });


        return new Promise<City | null>((resolve, reject) => {
            this.manager
                .get<any>(req)
                .then((res) => {
                    if (res == null) {
                        resolve(null);
                    } else {
                        resolve(new City(res));
                    }
                })
                .catch((err) => {
                    console.log(err);
                    reject(err);
                })
        });
    }
    unsuppressSeo(id: string): Promise<City | null> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: '/tools/misc/cities/' + encodeURIComponent(id),
            method: HttpMethod.PUT
        });


        return new Promise<City | null>((resolve, reject) => {
            this.manager
                .get<any>(req)
                .then((res) => {
                    if (res == null) {
                        resolve(null);
                    } else {
                        resolve(new City(res));
                    }
                })
                .catch((err) => {
                    console.log(err);
                    reject(err);
                })
        });
    }

    add(city: City): Promise<undefined> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: '/tools/misc/cities',
            method: HttpMethod.PUT
        }, city);

        return new Promise<undefined>((resolve, reject) => {
            this.manager
                .send(req)
                .then((res) => {
                    if (res.ok) {
                        resolve(undefined);
                    } else {
                        reject(res.statusText);
                    }
                })
                .catch((err) => {
                    console.log(err);
                    reject(err);
                })
        });
    }
    delete(id: string): Promise<undefined> {
        let req = this.manager.createRequest({
            service: this.manager.api,
            path: '/tools/misc/cities/' + encodeURIComponent(id),
            method: HttpMethod.DELETE
        });

        return new Promise<undefined>((resolve, reject) => {
            this.manager
                .send(req)
                .then((res) => {
                    if (res.ok) {
                        resolve(undefined);
                    } else {
                        reject(res.statusText);
                    }
                })
                .catch((err) => {
                    console.log(err);
                    reject(err);
                })
        });
    }
}

export default (framework: IFramework) => {
    framework.registerTransient<ICityManager>('ICityManager', (frm) => {
        return new CityManager(frm);
    });
}

